import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "You don't want to build your own list virtualization",
  "description": "",
  "published": "2018-02-01T00:00:00.000Z",
  "redirect_from": ["/blog/build-list-virtualization/swizec/8167"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Chatroom is slow? I know, I'll just use list virtualization!`}</p>
    <p>{`Well now you have two problems.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`So I've been working on this scroll UX today and it's surprisingly hard to get this right. `}<br parentName="p"></br><br parentName="p"></br>{`Did you know mouse wheel scroll deltaY can jump as much as 2000px in a single event? `}<a parentName="p" {...{
          "href": "https://t.co/HnC3AOqNVe"
        }}>{`pic.twitter.com/HnC3AOqNVe`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/958840988495093760"
      }}>{`January 31, 2018`}</a></blockquote>
    <p>{`List virtualization is hard, dear reader. `}<em parentName="p">{`So`}</em>{` hard. “Pfft, I can build that in an hour,” I thought until I tried.`}</p>
    <p>{`It's been 3 days. Last night, I dreamed scrollbars and mouse wheel events.`}</p>
    <h2 {...{
      "id": "hold-up-whats-list-virtualization"
    }}>{`Hold up, what's list virtualization?`}</h2>
    <p><a parentName="p" {...{
        "href": "https://www.google.com/search?q=list+virtualization&oq=list+virtualization&aqs=chrome..69i57j0l5.2398j0j1&sourceid=chrome&ie=UTF-8"
      }}>{`List virtualization`}</a>{` is a performance improvement technique for large lists or tables. Whenever your UI becomes slow because you're rendering too much stuff, you can use list virtualization to make it fast again.`}</p>
    <p>{`We've been hit with this problem recently at `}<a parentName="p" {...{
        "href": "https://www.yup.com"
      }}>{`Yup`}</a>{`. When our sessions get long, like hundreds of messages between tutor and student, tutors start complaining about the UI feeling sluggish.`}</p>
    <p>{`They can't type, they can't talk, it's like teaching through a paper bag. Terrible.`}</p>
    <p>{`We don't know why it became such a big problem right now, but 600 milliseconds to add a message to a chatroom is just too much. Needs to be fixed.`}</p>
    <p>{`That's where list virtualization comes in.`}</p>
    <p>{`Instead of keeping the whole hundreds-of-messages chatroom rendered, you render just 30 messages. Or 40, or 10. Whatever the number, you render a small subset of the messages.`}</p>
    <p>{`And that gives you a performance boost. Fewer DOM nodes to deal with, faster rendering times. Especially on slow computers.`}</p>
    <p>{`The problem may have become a problem because of the `}<a parentName="p" {...{
        "href": "https://techcrunch.com/2018/01/03/a-major-kernel-vulnerability-is-going-to-slow-down-all-intel-processors-2/"
      }}>{`Spectre and Meltdown fixes that hit Intel processors with up to 30% slowdowns`}</a>{`. When you're already using a slow $300 computer… yeah.`}</p>
    <h2 {...{
      "id": "how-to-virtualize-a-list-in-theory"
    }}>{`How to virtualize a list, in theory`}</h2>
    <p>{`So, how do you virtualize a list?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/02/Image-uploaded-from-iOS-1-1-1024x768.jpg",
        "alt": null
      }}></img></p>
    <p>{`Virtualizing a list is simple in theory. You maintain a window and move it around your list.`}</p>
    <p>{`A `}<inlineCode parentName="p">{`windowSize`}</inlineCode>{` variable tells you how many nodes you're rendering, and a `}<inlineCode parentName="p">{`windowIndex`}</inlineCode>{` variable tells you where to start rendering. Then you `}<inlineCode parentName="p">{`.splice`}</inlineCode>{` your data array and render away.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Building something in Backbone that would've been 5 seconds in React.`}<br parentName="p"></br><br parentName="p"></br>{`List windowing ...`}<br parentName="p"></br><br parentName="p"></br>{`React:`}<br parentName="p"></br>{`this.elements.splice(start, end).map()`}<br parentName="p"></br>{`detect scroll, change start/end. Done.`}<br parentName="p"></br><br parentName="p"></br>{`Backbone:`}<br parentName="p"></br>{`So if you go up you prepend elements and if you go down you append them and you ... `}<a parentName="p" {...{
          "href": "https://t.co/gE6XV140ld"
        }}>{`pic.twitter.com/gE6XV140ld`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/958497773749395456"
      }}>{`January 31, 2018`}</a></blockquote>
    <p>{`I was doing it in `}<a parentName="p" {...{
        "href": "http://backbonejs.org/"
      }}>{`Backbone`}</a>{`, so it seemed super tricky. We had complex logic in place to append and remove and prepend messages to the list as necessary.`}</p>
    <p>{`Think things like`}</p>
    <ol>
      <li parentName="ol">{`Find previous message with `}<inlineCode parentName="li">{`$("nth-child")`}</inlineCode></li>
      <li parentName="ol">{`Manipulate with `}<inlineCode parentName="li">{`$(ul).append`}</inlineCode></li>
      <li parentName="ol">{`Find the correct scroll position`}</li>
    </ol>
    <p>{`Messy.`}</p>
    <p>{`After a few hours of that approach, I gave up.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Screw DOM manipulation, it's 2018 and the DOM is fast. Re-render it.`}</p>
    </blockquote>
    <p>{`To my surprise, throwing away the list and re-rendering for every message insertion or scroll event works friggin’ great. Like really seriously great. Even in Backbone where you get none of React's diffing magic.`}</p>
    <p>{`I didn't run proper benchmarks, but on my 2017 MBP at home, re-rendering a 30-element list happens in 10 to 15 milliseconds.`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`$("ul").html("")`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`elements.splice(windowIndex, windowSize)`}</inlineCode></li>
      <li parentName="ol">{`loop and append`}</li>
    </ol>
    <p>{`That re-renders in just 10 milliseconds on my 15" MBP, 30 milliseconds on my 13" MBP. Both running RoR and sidekiq and webpack and the rest in the background.`}</p>
    <p>{`13" is so much slower because it's just dual core. I'll run a proper benchmark for this soon because I'm curious.`}</p>
    <p>{`My point is that re-rendering is fast and easy. You can totally get away with re-rendering on every mousewheel event if your `}<inlineCode parentName="p">{`windowSize`}</inlineCode>{` is small enough.`}</p>
    <p>{`I now had a virtualized list. Worked great.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Is this a confusing scroll experience? `}<a parentName="p" {...{
          "href": "https://t.co/wyHsu3C2IY"
        }}>{`pic.twitter.com/wyHsu3C2IY`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/958518929504088064"
      }}>{`January 31, 2018`}</a></blockquote>
    <p>{`Except the UX is confusing. You get to the edge of the scrollbar and it just keeps going and going. Wat?`}</p>
    <h2 {...{
      "id": "where-things-get-tricky"
    }}>{`Where things get tricky`}</h2>
    <p>{`This strange scrolling UX is where things go belly up and life gets hard. Users don't expect to hit the edge of the scrollbar before the edge of the content, you see.`}</p>
    <p>{`So I tried a couple things and haven't really figured this out. I think I'm on the right path, but I'm also starting to lose my mind.`}</p>
    <p>{`Watch this gif. Scrolling up works great, but then you start scrolling down and everything goes topsy turvy.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`So I've been working on this scroll UX today and it's surprisingly hard to get this right. `}<br parentName="p"></br><br parentName="p"></br>{`Did you know mouse wheel scroll deltaY can jump as much as 2000px in a single event? `}<a parentName="p" {...{
          "href": "https://t.co/HnC3AOqNVe"
        }}>{`pic.twitter.com/HnC3AOqNVe`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/958840988495093760"
      }}>{`January 31, 2018`}</a></blockquote>
    <p>{`The version in this gif tries to compensate for a few things.`}</p>
    <ol>
      <li parentName="ol">{`Uses an offset from the edge to start adding messages before you hit the edge`}</li>
      <li parentName="ol">{`Adjusts this offset to your scrolling speed`}</li>
      <li parentName="ol">{`Adjusts `}<inlineCode parentName="li">{`windowIndex`}</inlineCode>{` delta to your scrolling speed`}</li>
    </ol>
    <p>{`Adding messages is basically just moving the `}<inlineCode parentName="p">{`windowIndex`}</inlineCode>{`. That part is easy.`}</p>
    <p>{`But the mousewheel is a tricky beast. On my mouse, the smallest `}<inlineCode parentName="p">{`deltaY`}</inlineCode>{` is 4 pixels. That's okay.`}</p>
    <p>{`The biggest `}<inlineCode parentName="p">{`deltaY`}</inlineCode>{` I've seen is around 3000 pixels. In a single event call.`}</p>
    <p><img parentName="p" {...{
        "src": "/fe91a3dc38b1981226c5c025fc759674/media-xT0xeJpnrWC4XWblEk-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`So that's what I've been dealing with. I wish I could just use `}<a parentName="p" {...{
        "href": "https://github.com/bvaughn/react-virtualized"
      }}>{`react-virtualized`}</a>{`, but I can't because `}<em parentName="p">{`"We'll have time to rewrite later"`}</em>{`. Can't come soon enough 😅`}</p>
    <h2 {...{
      "id": "update-omg-i-did-it"
    }}>{`Update: OMG I DID IT`}</h2>
    <p>{`It's not as smooth as Slack, but it's good enough for now.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`OMG I DID IT! `}<a parentName="p" {...{
          "href": "https://t.co/s85429V067"
        }}>{`pic.twitter.com/s85429V067`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/959151765185691650"
      }}>{`February 1, 2018`}</a></blockquote>
    <p>{`Wasn't that complicated after all. Just a bunch of maths with magic numbers.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/02/carbon-2-1024x1013.png",
        "alt": null
      }}></img></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      